import { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

/** True when a Turnstile site key is configured. Callers require a solved token
 * only when this is true, so forms stay usable before keys are set. */
export const isTurnstileEnabled = !!SITE_KEY;

interface TurnstileWindow {
  turnstile?: {
    render: (el: HTMLElement, opts: Record<string, unknown>) => string;
    remove: (id: string) => void;
  };
}

function loadScript(): Promise<void> {
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile script failed"));
    document.head.appendChild(s);
  });
}

interface Props {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  /** Fired when the widget script can't load (blocked/offline). Submit stays
   *  disabled either way — this lets the caller tell the user why. */
  onError?: () => void;
}

/**
 * Cloudflare Turnstile widget. Renders nothing when no site key is configured
 * (local dev without `VITE_TURNSTILE_SITE_KEY`) — callers should keep submit
 * disabled until `onVerify` fires.
 */
export function Turnstile({ onVerify, onExpire, onError }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!SITE_KEY || !ref.current) return;
    let widgetId: string | undefined;
    let cancelled = false;
    const el = ref.current;

    loadScript()
      .then(() => {
        const w = window as unknown as TurnstileWindow;
        if (cancelled || !w.turnstile) return;
        widgetId = w.turnstile.render(el, {
          sitekey: SITE_KEY,
          callback: (token: string) => onVerify(token),
          "expired-callback": () => onExpire?.(),
        });
      })
      .catch(() => {
        // script blocked / offline — submit stays disabled, fail closed.
        if (!cancelled) onError?.();
      });

    return () => {
      cancelled = true;
      const w = window as unknown as TurnstileWindow;
      if (widgetId && w.turnstile) w.turnstile.remove(widgetId);
    };
  }, [onVerify, onExpire, onError]);

  if (!SITE_KEY) return null;
  return <div ref={ref} />;
}
